import { faCircleXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Popover, Typography, useTheme } from "@mui/material"
import { translate } from "app/language/service"
import { areSameDate, getDateFnsLocale } from "app/util/date/tools/common"
import { getFilterPeriodLabel } from "campaign/tools/filters"
import { addDays } from "date-fns"
import React from "react"
import { DateRangePicker, Range, StaticRange } from "react-date-range"
import { IPeriodFilter, NO_FILTER_KEY, getStartEndFilters } from "ui/model"

// We use a nice react component that manage the range:
//  => https://hypeserver.github.io/react-date-range/#daterangepicker

interface ITimePeriodProps {
  id: string
  label: string
  value: IPeriodFilter
  sx?: any
  onChange(newValue: IPeriodFilter, filterLabelKey?: string): any
}

export const TimePeriod = React.memo(({ id, label, value, onChange, sx }: ITimePeriodProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)
  const uiTheme = useTheme()
  const open = Boolean(anchorEl)
  const isFilterActive: boolean = value.startDate !== null || value.endDate !== null
  const allFilters = getStartEndFilters()
  const staticRanges: StaticRange[] = allFilters.map(f => ({
    label: translate(f.labelKey),
    isSelected: (range: Range) => areSameDate(range.startDate, f.startDate) && areSameDate(range.endDate, f.endDate),
    range: () => ({
      startDate: f.startDate || undefined,
      endDate: f.endDate || undefined
    })
  }))
  const getActiveFilterKey = (
    startDate: Date | null | undefined,
    endDate: Date | null | undefined,
    withEmptyKey?: boolean
  ): string | undefined => {
    for (const filter of allFilters) {
      if (
        startDate !== null &&
        endDate !== null &&
        areSameDate(filter.startDate, startDate) &&
        areSameDate(filter.endDate, endDate)
      ) {
        if (filter.labelKey === NO_FILTER_KEY && !withEmptyKey) {
          return
        }
        return filter.labelKey
      }
    }
  }
  return (
    <>
      <Box
        id={id}
        sx={{
          ...sx,
          "position": "relative",
          "borderRadius": ".25rem",
          "height": "2.5rem",
          "minHeight": "2.5rem",
          "maxHeight": "2.5rem",
          "boxShadow": theme => theme.palette.boxShadow,
          "display": "flex",
          "alignItems": "center",
          "justifyContent": "space-between",
          "cursor": "pointer",
          "border": theme => (open ? `1px solid ${theme.palette.primary.main}` : theme.palette.border),
          "&:hover": {
            border: theme => (open ? `1px solid ${theme.palette.primary.main}` : "1px solid black")
          },
          "& .period-clear-icon": {
            ml: "1rem",
            mr: ".5rem",
            cursor: "pointer",
            color: theme => theme.palette.primary.main,
            animation: "fadeIn 600ms"
          }
        }}
        onClick={event => setAnchorEl(event.currentTarget)}>
        <Typography
          color="textSecondary"
          sx={{
            fontSize: ".56rem",
            position: "absolute",
            top: "-.35rem",
            left: ".5rem",
            pl: ".2rem",
            pr: ".2rem",
            lineHeight: ".8rem",
            backgroundColor: theme => theme.palette.background.default
          }}>
          {label}
        </Typography>
        <Typography color="textPrimary" sx={{ fontSize: ".8rem", pl: ".5rem", pt: ".3rem" }}>
          {getFilterPeriodLabel(value)}
        </Typography>
        {!open && isFilterActive && (
          <FontAwesomeIcon
            icon={faCircleXmark}
            onClick={event => {
              event.stopPropagation()
              onChange({ startDate: null, endDate: null })
            }}
            className="period-clear-icon"
          />
        )}
      </Box>
      <Popover
        id={open ? `${id}-popover` : undefined}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        sx={{
          "border": theme => `1px solid ${theme.palette.primary.main}`,
          "& .rdrDefinedRangesWrapper, & .rdrStaticRange, & .rdrCalendarWrapper, & .rdrDateRangeWrapper": {
            backgroundColor: theme => theme.palette.background.default,
            color: theme => theme.palette.text.primary
          },
          "& .rdrDateDisplayWrapper": {
            backgroundColor: theme => theme.palette.background.paper,
            minWidth: "18.5rem",
            position: "absolute",
            top: 0,
            left: "2.9rem"
          },
          "& .rdrDateDisplayItem, & .rdrDateDisplayItemActive input, & .rdrDateDisplayItem input": {
            backgroundColor: theme => theme.palette.background.default,
            color: theme => theme.palette.text.primary,
            borderRadius: ".25rem"
          },
          "& .rdrMonthAndYearPickers select": {
            color: theme => theme.palette.text.primary,
            fontSize: ".8rem",
            padding: "10px 10px 10px 10px",
            background: "none"
          },
          "& .rdrMonthAndYearPickers select:hover": {
            backgroundColor: theme => theme.palette.background.paper
          },
          "& .rdrDayNumber span": {
            color: theme => theme.palette.text.primary
          },
          "& .rdrDayPassive .rdrDayNumber span": {
            color: "rgba(155, 155, 155, 0.5)"
          },
          "& .rdrDefinedRangesWrapper": {
            width: "10rem",
            mt: "3.45rem",
            borderRight: theme => theme.palette.border
          },
          "& .rdrStaticRange": {
            borderBottom: theme => theme.palette.border
          },
          "& .rdrStaticRangeLabel": {
            fontSize: ".7rem"
          },
          "& .rdrStaticRangeLabel:hover": {
            backgroundColor: theme => theme.palette.background.paper
          },
          "& .rdrMonth": {
            width: "14rem",
            paddingBottom: ".25rem"
          },
          "& .rdrCalendarWrapper.rdrDateRangeWrapper": {
            mt: "3.45rem"
          },
          "& .rdrPprevButton": {
            mr: ".0625rem"
          },
          "& .rdrNextButton": {
            ml: ".0625rem"
          },
          "& .rdrMonthPicker, & .rdrYearPicker": {
            m: ".0625rem"
          },
          "& .rdrDay": {
            height: "1.8rem"
          },
          "& .rdrDayDisabled": {
            backgroundColor: "transparent",
            opacity: 0.1
          },
          "& .rdrNextPrevButton": {
            backgroundColor: theme => theme.palette.background.paper
          },
          "& .rdrNextPrevButton i": {
            color: theme => theme.palette.action.active
          },
          "& .rdrMonthAndYearPickers option": {
            backgroundColor: theme => theme.palette.background.default,
            color: theme => theme.palette.text.primary
          },
          "& .rdrDayToday .rdrDayNumber span:after": {
            bottom: ".1rem",
            width: "50%",
            background: theme => theme.palette.primary.main
          },
          "& .rdrDateDisplayItemActive": {
            borderColor: theme => theme.palette.primary.main
          }
        }}>
        <DateRangePicker
          onChange={item =>
            onChange(
              { startDate: item.selection.startDate || null, endDate: item.selection.endDate || null },
              getActiveFilterKey(item.selection.startDate, item.selection.endDate)
            )
          }
          moveRangeOnFirstSelection={false}
          months={2}
          shownDate={value.startDate || new Date()}
          ranges={[
            {
              startDate: value.startDate || addDays(new Date(), 1),
              endDate: value.endDate || addDays(new Date(), 1),
              key: "selection"
            }
          ]}
          showPreview={false}
          direction="vertical"
          rangeColors={[uiTheme.palette.primary.main as string]}
          staticRanges={staticRanges}
          inputRanges={[]}
          locale={getDateFnsLocale()}
        />
      </Popover>
    </>
  )
})
